import { Close, PersonOutlineOutlined } from '@mui/icons-material';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';

import TicketStatusChip from './ticketStatusChip';

import { ACTIONS } from 'constant';
import { TICKET_URGENCY_CONFIG } from 'data/constants/constants';
import { useAgents } from 'hooks/useAgents';
import useTranslate from 'hooks/useTranslate';
import { TicketInterface } from 'types';
import { formatDateString } from 'utils/formatDate';
import { getTicketUrgencyLabelKey } from 'utils/ticket';

interface ViewTicketProps {
    ticket: TicketInterface;
    onClose: () => void;
    handleAction: (action: ACTIONS, ticket: TicketInterface) => void;
}

const PersonInfo = ({ title, value }: { title: string; value: string }) => (
    <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5 }}
        >
            {title}
        </Typography>
        <Chip
            size="small"
            icon={<PersonOutlineOutlined sx={{ fontSize: 16 }} />}
            label={value || '—'}
            sx={{ fontWeight: 600, height: 32 }}
        />
    </Box>
);

const ViewTicket = ({ ticket, onClose, handleAction }: ViewTicketProps) => {
    const { translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const agentOptions = useAgents();

    const getAgentName = (id?: string | number) =>
        agentOptions.find((agent) => String(agent.value) === String(id))?.label || '—';

    const urgency = TICKET_URGENCY_CONFIG[ticket.urgency] ?? {
        label: ticket.urgency || '—',
        color: 'text.secondary',
        bg: 'action.hover',
    };

    return (
        <Dialog open fullWidth maxWidth="sm" fullScreen={isMobile} scroll="paper">
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 1,
                }}
            >
                <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                    <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
                        {translate('viewTicket')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {translate('ticketId').replace('{{id}}', String(ticket.id))}
                    </Typography>
                </Stack>
                <IconButton
                    onClick={onClose}
                    edge="end"
                    size="small"
                    aria-label={translate('close')}
                >
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        sx={{
                            p: 1.5,
                            alignItems: { xs: 'stretch', sm: 'center' },
                            justifyContent: 'space-between',
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                        }}
                    >
                        <Typography variant="body2">
                            {translate('ticketAttachedToDocument').replace(
                                '{{name}}',
                                ticket.document_name || '—'
                            )}
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleAction(ACTIONS.PREVIEW_FILE, ticket)}
                            sx={{ textTransform: 'none', flexShrink: 0 }}
                        >
                            {translate('viewDocument')}
                        </Button>
                    </Stack>

                    <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 700, textTransform: 'uppercase' }}
                        >
                            {translate('ticketTitle')}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                            {ticket.text || '—'}
                        </Typography>
                    </Box>

                    <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 700, textTransform: 'uppercase' }}
                        >
                            {translate('ticketDescription')}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {ticket.description || '—'}
                        </Typography>
                    </Box>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flex: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                {translate('status')}
                            </Typography>
                            <TicketStatusChip status={ticket.status} disabled />
                        </Stack>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flex: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                {translate('priority')}
                            </Typography>
                            <Chip
                                size="small"
                                label={translate(getTicketUrgencyLabelKey(ticket.urgency))}
                                sx={{
                                    fontWeight: 600,
                                    color: urgency.color,
                                    backgroundColor: urgency.bg,
                                }}
                            />
                        </Stack>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <Stack spacing={0.25} sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                {translate('createdAt')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {formatDateString(ticket.created) || translate('notAvailable')}
                            </Typography>
                        </Stack>
                        <Stack spacing={0.25} sx={{ flex: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                {translate('updatedAt')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {ticket.updated
                                    ? formatDateString(ticket.updated)
                                    : translate('notAvailable')}
                            </Typography>
                        </Stack>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <PersonInfo
                            title={translate('createdBy')}
                            value={getAgentName(ticket.created_by_id)}
                        />
                        <PersonInfo
                            title={translate('assignedTo')}
                            value={getAgentName(ticket.assigned_to_id)}
                        />
                    </Stack>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, gap: 1, justifyContent: 'space-between' }}>
                <Button
                    variant="outlined"
                    onClick={() => handleAction(ACTIONS.EDIT, ticket)}
                    sx={{ textTransform: 'none' }}
                >
                    {translate('editDetails')}
                </Button>
                <Button
                    variant="contained"
                    onClick={() => handleAction(ACTIONS.CHAT, ticket)}
                    sx={{ textTransform: 'none' }}
                >
                    {translate('ticketChat')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ViewTicket;
